import {
	LitElement,
	html,
	css,
	t,
	classMap,
	paths,
	unsafeHTML,
	broadcastReceiver,
} from '@hh/global.js'

const STOREFRONT_API_ENDPOINT = '/api/2023-01/graphql.json'
const STOREFRONT_API_KEY = window.HH.cartDiscount.apiKey

class HHDiscountInput extends LitElement {
	static properties = {
		discountCode: {
			type: String,
		},
		cartItems: {
			type: Array,
		},
		disableApply: {
			type: Boolean,
		},
		open: {
			type: Boolean,
		},
		validDiscountCode: {
			type: Boolean,
		},
		error: {
			type: Boolean,
		},
		errorMessage: {
			type: String,
		},
		loading: {
			type: Boolean,
		},
		discountSavings: {
			type: Number,
		},
	}

	constructor() {
		super()
		this.moneyFormat =
			window.theme.settings.money_with_currency_format || '${{amount}}'
		this.currencyCode = window.theme.settings.iso_code_currency
		this.countryCode = window.theme.settings.iso_code_country

		this.selectors = {
			cartDrawer: '#Cart-Drawer',
			cartFooter: '.cart-drawer-footer',
			originalSubtotal: '[data-original-subtotal]',
			discountedSubtotal: '[data-discounted-subtotal]',
			checkoutLink: '[data-checkout-link]',
			lineItemByIndex: (index) => `#CartDrawerItem-${index}`,
			originalItemPriceWrapper: '[data-original-item-price-wrapper]',
			discountItemPriceWrapper: '[data-discounted-item-price-wrapper]',
			discountedItemPrice: '[data-discounted-item-price]',
			itemDiscountMessage: '[data-item-discount-message]',
			itemDiscountCode: '[data-item-discount-code]',
			itemDiscountAmount: '[data-item-discount-amount]',
		}

		this.classes = {
			hide: 'hidden',
		}

		this.disableApply = true
		this.validDiscountCode = false
		this.open = false
		this.error = false
		this.loading = false
		this.discountSavings = 0
	}

	async connectedCallback() {
		super.connectedCallback()

		this.cartDrawer = document.querySelector(this.selectors.cartDrawer)
		this.cartFooter = document.querySelector(this.selectors.cartFooter)
		this.originalSubtotal = this.cartFooter.querySelector(
			this.selectors.originalSubtotal
		)
		this.discountedSubtotal = this.cartFooter.querySelector(
			this.selectors.discountedSubtotal
		)

		this.checkoutLink = this.cartFooter.querySelector(
			this.selectors.checkoutLink
		)
		this.originalCheckoutLink = this.checkoutLink.getAttribute('href')

		this.cartItems = this.formatCartItems(await this.getCartItems())
		this.discountCode = this.getDiscountCookie()

		if (this.discountCode) {
			this.discountCart = await this.createCart(
				this.cartItems,
				this.discountCode
			)
			this.applyCartDiscounts(this.discountCart)
		}

		this.receiver = broadcastReceiver('discount', (...args) => {
			this.getDiscountCode(...args)
		})
	}

	disconnectedCallback() {
		super.disconnectedCallback()
	}

	updated(changedProperties) {
		if (changedProperties.has('discountCode')) {
			this.updateCheckoutLink(this.discountCode)
		}
	}

	// Handlers
	async onSubmit(e) {
		e.preventDefault()
		this.loading = true
		const form = e.target
		const formData = new FormData(form)
		const discount = formData.get('discount')
		this.setDiscountCookie(discount)

		if (!discount) return

		this.discountCart = await this.createCart(this.cartItems, this.discountCode)
		this.applyCartDiscounts(this.discountCart)
	}

	async getDiscountCode(data) {
		this.discount = data.code
		const discount = this.discount
		this.setDiscountCookie(discount)

		if (!discount) return

		this.discountCart = await this.createCart(this.cartItems, this.discount)
		this.applyCartDiscounts(this.discountCart)
	}

	updateCheckoutLink(discountCode) {
		if (!discountCode) {
			this.checkoutLink.setAttribute('href', this.originalCheckoutLink)
			return
		}

		this.checkoutLink.setAttribute(
			'href',
			`${this.originalCheckoutLink}?discount=${discountCode}`
		)
	}

	onRemoveDiscount() {
		this.removeDiscountCookie()
	}

	onInput(e) {
		this.error = false
		const { value } = e.target
		this.disableApply = !value
	}

	onLabelClick() {
		this.open = true
	}

	// Requests
	async getCartItems() {
		try {
			const res = await fetch('/cart.js')
			if (!res.ok) {
				throw new Error('Network response was not ok')
			}

			const data = await res.json()
			return data.items
		} catch (error) {
			console.error(error)
		}
	}

	formatCartItems(items) {
		const variantGidPrefix = 'gid://shopify/ProductVariant/'
		return items.map((item) => {
			return {
				merchandiseId: `${variantGidPrefix}${item.variant_id}`,
				quantity: item.quantity,
			}
		})
	}

	async createCart(items, discountCode) {
		const variables = {
			input: {
				lines: items,
				discountCodes: discountCode,
				buyerIdentity: {
					countryCode: window.Shopify?.country || 'US',
				},
			},
		}

		try {
			const res = await fetch(STOREFRONT_API_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
				},
				body: JSON.stringify({
					query: CREATE_CART_QUERY,
					variables,
				}),
			})

			if (!res.ok) {
				throw new Error('Network response was not ok')
			}

			const data = await res.json()
			const errors = data.data.cartCreate.userErrors
			if (errors.length > 0) {
				console.error(errors)
				throw new Error(errors[0].message)
			}

			return data.data.cartCreate.cart
		} catch (error) {
			console.error(error)
			this.throwDiscountError(
				error + ' Remove the product from cart to apply discount.'
			)
		}
	}

	// Apply discount functions
	applyCartDiscounts(cart) {
		this.loading = false
		const hasApplicableDiscounts = cart?.discountCodes[0]?.applicable === true
		if (!hasApplicableDiscounts) {
			this.validDiscountCode = false
			this.discountSavings = 0
			if (cart != undefined) {
				this.throwDiscountError('Invalid discount code')
			}
			this.removeDiscountCookie()
			return
		}
		this.validDiscountCode = true
		this.updateSubtotal(cart)
		this.checkLineItems(cart)
	}

	updateSubtotal(cart) {
		const originalAmount = cart?.cost?.subtotalAmount?.amount
		const discountedAmount = cart?.cost?.totalAmount?.amount

		if (discountedAmount) {
			this.discountedSubtotal.classList.remove(this.classes.hide)
			if (this.countryCode != 'US') {
				this.discountedSubtotal.textContent =
					formatMoney(discountedAmount * 100, this.moneyFormat) +
					' ' +
					this.currencyCode
			} else {
				this.discountedSubtotal.textContent = formatMoney(
					discountedAmount * 100,
					this.moneyFormat
				)
			}
			this.originalSubtotal.classList.add(this.classes.hide)
		}

		if (originalAmount > discountedAmount) {
			this.discountSavings = originalAmount - discountedAmount
		}

		dispatchCustomEvent('cart:discount-applied', {
			subtotal: discountedAmount,
			newcart: cart,
		})
	}

	checkLineItems(cart) {
		const lineItems = cart?.lines?.nodes

		lineItems.forEach((item, itemIndex) => {
			const itemDiscountAllocations = item.discountAllocations
			if (itemDiscountAllocations.length > 0) {
				this.checkCodeDiscountAllocations(
					item,
					itemIndex,
					itemDiscountAllocations
				)
			}
		})
	}

	checkCodeDiscountAllocations(item, itemIndex, discountAllocations) {
		discountAllocations.forEach((allocation) => {
			const isCodeDiscount =
				allocation.__typename === 'CartCodeDiscountAllocation'
			if (isCodeDiscount) {
				this.updateLineItems(item, itemIndex, allocation)
			}
		})
	}

	updateLineItems(item, itemIndex, allocation) {
		const lineItem = this.cartDrawer.querySelector(
			this.selectors.lineItemByIndex(itemIndex + 1)
		)
		if (!lineItem) return

		const originalPriceWrapper = lineItem.querySelector(
			this.selectors.originalItemPriceWrapper
		)
		const discountedPriceWrapper = lineItem.querySelector(
			this.selectors.discountItemPriceWrapper
		)
		const discountedPrice = lineItem.querySelector(
			this.selectors.discountedItemPrice
		)
		const discountMessage = lineItem.querySelector(
			this.selectors.itemDiscountMessage
		)
		const discountCode = lineItem.querySelector(this.selectors.itemDiscountCode)
		const discountAmount = lineItem.querySelector(
			this.selectors.itemDiscountAmount
		)

		originalPriceWrapper?.classList.add(this.classes.hide)
		discountedPriceWrapper?.classList.remove(this.classes.hide)

		if (discountedPrice) {
			discountedPrice.textContent = formatMoney(
				item.cost.totalAmount.amount * 100,
				this.moneyFormat
			)
		}

		discountMessage?.classList.remove(this.classes.hide)
		discountMessage?.parentElement?.classList.remove(this.classes.hide)

		if (discountCode && discountAmount) {
			discountCode.textContent = allocation?.code
			discountAmount.textContent = formatMoney(
				allocation?.discountedAmount?.amount * 100,
				this.moneyFormat
			)
		}
	}

	revertToOriginalPrices() {
		this.discountedSubtotal.classList.add(this.classes.hide)
		this.originalSubtotal.classList.remove(this.classes.hide)

		const originalPriceWrappers = this.cartDrawer.querySelectorAll(
			this.selectors.originalItemPriceWrapper
		)
		const discountedPriceWrappers = this.cartDrawer.querySelectorAll(
			this.selectors.discountItemPriceWrapper
		)
		const discountMessages = this.cartDrawer.querySelectorAll(
			this.selectors.itemDiscountMessage
		)

		const discountMessageParents = [...discountMessages].map((message) => {
			const parent = message.parentElement
			if (!parent.hasAttribute('data-original-discounts')) return parent
		})

		const discountItemElements = [
			...originalPriceWrappers,
			...discountedPriceWrappers,
			...discountMessages,
			...discountMessageParents,
		]

		discountItemElements.forEach((element) =>
			element?.classList.add(this.classes.hide)
		)

		originalPriceWrappers.forEach((element) =>
			element?.classList.remove(this.classes.hide)
		)

		dispatchCustomEvent('cart:discount-removed')
	}

	throwDiscountError(message) {
		this.error = true
		this.errorMessage = message

		setTimeout(() => {
			this.error = false
			this.errorMessage = ''
		}, 8000)
	}

	// Discount code functions
	getDiscountCookie() {
		const discountCookie = document.cookie
			?.split('; ')
			?.find((row) => row.startsWith('discount_code='))
			?.split('=')[1]

		if (!discountCookie) {
			return null
		}

		return discountCookie
	}

	setDiscountCookie(discountCode) {
		document.cookie = `discount_code=${discountCode}; path=/; max-age=Session; SameSite=Lax;`
		this.discountCode = discountCode
		let discountAdded = `{"codeRemoved": "false"}`
		document.body.setAttribute('discount-removal', discountAdded)
	}

	removeDiscountCookie() {
		document.cookie = `discount_code=; path=/; max-age=0; SameSite=Lax;`
		this.disableApply = true
		this.validDiscountCode = false
		this.discountCode = null
		this.discountSavings = 0
		this.revertToOriginalPrices()
		let discountRemoval = `{"codeRemoved": "true"}`
		document.body.setAttribute('discount-removal', discountRemoval)
		let code = `{"code": ""}`
		document.body.setAttribute('discount', code)
	}

	render() {
		return html/*html*/ `
			<div class="discount__container">
				<div
					class=${classMap({
						discount__error: true,
						hidden: !this.error,
					})}
				>
					${this.errorMessage}
				</div>
				<form action="POST" @submit=${this.onSubmit}>
					<div class="discount__label-wrapper">
						<label
							for="discount_apply"
							class=${classMap({
								discount__label: true,
								'discount__label--open': this.open,
							})}
							@click=${this.onLabelClick}
						>
							${t('general.cart_drawer.discount_code')}&nbsp;&nbsp;
							<span
								class=${classMap({
									hidden: this.open,
								})}
								>${unsafeHTML(paths.plus)}</span
							>
						</label>
						<div
							class=${classMap({
								'discount__applied-code': true,
								hidden: !this.validDiscountCode,
							})}
						>
							<span>${this.discountCode}</span>
							<button
								type="button"
								class="discount__remove"
								aria-label=${t('general.cart_drawer.remove_discount')}
								@click=${this.onRemoveDiscount}
							>
								${unsafeHTML(paths.remove)}
							</button>
						</div>
					</div>

					<div
						class=${classMap({
							'discount__input-wrapper': true,
							hidden: !this.open || this.validDiscountCode,
						})}
					>
						<input
							type="text"
							name="discount"
							id="discount_apply"
							class="discount__input"
							?readonly=${this.discountCode ? true : false}
							.value=${this.discountCode ? this.discountCode : null}
							@input=${this.onInput}
						/>
						<button
							type="submit"
							class="${classMap({
								discount__submit: true,
								hidden: !this.loading && this.discountCode,
							})}"
							?disabled=${this.disableApply}
						>
							<span
								class=${classMap({
									hidden: this.loading,
								})}
							>
								${t('general.cart_drawer.apply_discount')}
							</span>
							<span
								class=${classMap({
									discount__loading: true,
									hidden: !this.loading,
								})}
							>
								${unsafeHTML(paths.spinner)}
							</span>
						</button>
					</div>
				</form>
				<div
					class=${classMap({
						discount__savings: true,
						hidden: !this.validDiscountCode || this.discountSavings === 0,
					})}
				>
					<span>${t('general.cart_drawer.savings')}</span>
					<span
						>-${formatMoney(this.discountSavings * 100, this.moneyFormat)}</span
					>
				</div>
			</div>
		`
	}

	static styles = css`
		:host {
			font-family: var(--font-body-family);
			box-sizing: border-box;
		}

		.discount__container {
			margin: 0.875rem 0 0.5rem;
		}

		.hidden {
			display: none !important;
		}

		.discount__input,
		.discount__label {
			display: block;
		}

		.discount__label-wrapper {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 0.5rem;
		}

		.discount__label {
			font-size: 0.75rem;
			color: var(--color-body);
		}

		.discount__label:not(.discount__label--open) {
			cursor: pointer;
		}

		.discount__applied-code {
			display: flex;
			justify-content: center;
			gap: 0.625rem;
			align-items: center;
			color: #808080;
		}

		.discount__remove {
			border: none;
			background: transparent;
			padding: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: currentColor;
		}

		.discount__input-wrapper {
			display: flex;
			border-bottom: 1px solid #aaaaaa;
		}

		.discount__input {
			width: 100%;
			border: none;
			font-size: 1rem;
			padding: 0.3125rem;
		}

		.discount__input:focus {
			outline: none;
		}

		.discount__submit {
			border: none;
			background: transparent;
			font-size: 0.875rem;
			font-weight: 600;
			text-transform: uppercase;
			min-width: 4rem;
			white-space: nowrap;
		}

		.discount__submit:not([disabled]) {
			cursor: pointer;
			color: var(--color-body);
		}

		.discount__error {
			text-align: center;
			color: #e93636;
			margin-bottom: 0.875rem;
		}

		.discount__loading {
			width: 18px;
			height: 18px;
			display: block;
			margin: 0 auto;
		}

		.discount__savings {
			display: flex;
			justify-content: space-between;
			font-size: 0.875rem;
		}

		.spinner {
			animation: rotator 1.4s infinite linear;
		}

		.spinner-path {
			stroke-dasharray: 280;
			stroke-dashoffset: 0;
			transform-origin: center;
			animation: dash 1.4s infinite ease;
		}

		@keyframes rotator {
			0% {
				transform: rotate(0deg);
			}
			100% {
				transform: rotate(270deg);
			}
		}

		@keyframes dash {
			0% {
				stroke-dashoffset: 280;
			}
			50% {
				stroke-dashoffset: 75;
				transform: rotate(135deg);
			}
			100% {
				stroke-dashoffset: 280;
				transform: rotate(450deg);
			}
		}
	`
}

customElements.define('hh-discount-input', HHDiscountInput)

const CREATE_CART_QUERY = /* GraphQL */ `
	mutation cartCreate($input: CartInput) {
		cartCreate(input: $input) {
			cart {
				id
				checkoutUrl
				cost {
					totalAmount {
						amount
						currencyCode
					}
					subtotalAmount {
						amount
						currencyCode
					}
					checkoutChargeAmount {
						amount
						currencyCode
					}
				}
				discountCodes {
					applicable
					code
				}
				lines(first: 250) {
					nodes {
						quantity
						cost {
							subtotalAmount {
								amount
								currencyCode
							}
							totalAmount {
								amount
								currencyCode
							}
						}
						discountAllocations {
							... on CartCodeDiscountAllocation {
								__typename
								code
								discountedAmount {
									amount
									currencyCode
								}
							}
							... on CartAutomaticDiscountAllocation {
								__typename
								title
								discountedAmount {
									amount
									currencyCode
								}
							}
							... on CartCustomDiscountAllocation {
								__typename
								title
								discountedAmount {
									amount
									currencyCode
								}
							}
						}
						merchandise {
							... on ProductVariant {
								product {
									title
									tags
								}
								title
							}
						}
					}
				}
			}
			userErrors {
				field
				message
			}
		}
	}
`
